// 🔴 L2 (N°876) — FICHE DOSSIER ARTISAN : un dossier = UN client + UN site.
//
// ⚠️ Ce qu'elle remplace, pour un tenant qui a ADOPTÉ le modèle Site : la fiche
// TEF/AMO filtrée par une quinzaine de gates (zone MÉLANGÉE, N°856).
// Construite comme `DossierOmvView` (une vue dédiée qui assemble des composants
// CONTRÔLÉS), ⛔ pas en déformant la fiche TEF ni celle d'OMV.
//
// COMPOSANTS — dépendances RÉELLES vérifiées (N°867) :
//   RÉUTILISÉS : DocumentsForm (/metier/dossiers/{id}/documents, /metier/documents/*,
//     /metier/config/document-types, /documents/validate) · CommentairesDossier
//     (/metier/dossiers/{id}/commentaires) · HistoriqueModal (aucune route).
//   EXTRAIT : ListeDevisDossier. RECONSTRUITS : SuiviEtapesArtisan, sites.
//   ⛔ JAMAIS : EnvoyerVisiteBlock, VisitesDuDossier, ni aucune route du DAL gelé.
import { useEffect, useState } from "react";
import { useConfigList } from "../../../hooks/useConfigList";
import { estPersonneMorale, formatClientAddress, formatClientFullName, formatSiteAddress, libelleTypeClient } from "../../../utils/clientFormat";
import { SitesDuClient } from "./SitesDuClient";
import { DocumentsForm } from "../dossier/DocumentsForm";
import { CommentairesDossier } from "../dossier/CommentairesDossier";
import { HistoriqueModal } from "../dossier/HistoriqueModal";
import { SiteForm } from "./SiteForm";
import { SuiviEtapesArtisan } from "./SuiviEtapesArtisan";
import { ListeDevisDossier } from "./ListeDevisDossier";
import { EmplacementVisites } from "./EmplacementVisites";
import { creerSite, lireSite, rattacherSite, sitesDuClient } from "./api";

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                borderRadius: 12, padding: 16 };

function BlocSite({ dossier, dossierId, onUpdate }) {
  const [site, setSite] = useState(null);
  const [candidats, setCandidats] = useState(null);
  const [creation, setCreation] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    setErreur(null);
    if (dossier?.site_id) {
      lireSite(dossier.site_id).then(setSite).catch((e) => setErreur(e.message));
    } else if (dossier?.client_id) {
      sitesDuClient(dossier.client_id).then((r) => setCandidats(r.sites || []))
        .catch((e) => { setErreur(e.message); setCandidats([]); });
    }
  }, [dossier?.site_id, dossier?.client_id]);

  const rattacher = async (siteId) => {
    setOccupe(true); setErreur(null);
    try { await rattacherSite(dossierId, siteId); onUpdate && onUpdate(); }
    catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  return (
    <div data-testid="artisan-site">
      <div className="text-xs mb-1" style={{ color: "var(--oh-text-secondary)" }}>Site d'intervention de ce dossier</div>
      {erreur && <div className="text-sm mb-2" role="alert" style={{ color: "var(--oh-error-text)" }}>{erreur}</div>}
      {dossier?.site_id ? (
        site && (
          // 🔴 N°915-A — UNE SEULE ligne d'adresse, et UNE SEULE FOIS.
          //   ① La concaténation locale affichait « rue, CP ville » alors que `adresse`
          //      contenait DÉJÀ le CP et la ville ⇒ « …75006 Paris, 75006 Paris »
          //      (N°914-A). `formatSiteAddress` normalise avant de composer, donc les
          //      dossiers DÉJÀ enregistrés redondants s'affichent juste — ⛔ sans
          //      réécrire la base.
          //   ② Sans `nom_site`, l'ancienne version montrait l'adresse DEUX FOIS (en
          //      titre puis en détail). Le titre ⛔ ne répète plus le détail.
          //   🔑 Ce bloc dit QUEL site porte CE dossier (un dossier = un lieu) ; la
          //      GESTION de la liste reste la section « Sites / Adresses d'intervention ».
          <div data-testid="artisan-site-rattache">
            {site.nom_site && (
              <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>{site.nom_site}</div>
            )}
            <div className={site.nom_site ? "text-sm" : ""} data-testid="artisan-site-adresse"
                 style={site.nom_site
                   ? { color: "var(--oh-text-secondary)" }
                   : { color: "var(--oh-text)", fontWeight: 500 }}>
              {formatSiteAddress(site) || "Adresse non renseignée"}
            </div>
          </div>
        )
      ) : (
        <div data-testid="artisan-site-a-renseigner">
          <div className="text-sm mb-2" style={{ color: "var(--oh-stat-amber-text)" }}>
            Site à renseigner : un dossier concerne toujours un lieu.
          </div>
          {(candidats || []).map((s) => (
            <button key={s.id} type="button" disabled={occupe} data-testid="artisan-site-choisir"
                    onClick={() => rattacher(s.id)}
                    className="block w-full text-left text-sm rounded-lg px-3 py-2 mb-1 disabled:opacity-60"
                    style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)",
                             color: "var(--oh-text)" }}>
              {s.nom_site || s.adresse} — {s.ville}
            </button>
          ))}
          {creation ? (
            <SiteForm occupe={occupe} libelleBouton="Créer et rattacher" onAnnuler={() => setCreation(false)}
                      onValider={async (data) => {
                        setOccupe(true); setErreur(null);
                        try {
                          const s = await creerSite(dossier.client_id, data);
                          await rattacherSite(dossierId, s.id);
                          onUpdate && onUpdate();
                        } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
                      }} />
          ) : (
            <button type="button" onClick={() => setCreation(true)} data-testid="artisan-site-creer"
                    className="rounded-lg px-3 text-sm mt-1"
                    style={{ height: 36, background: "transparent",
                             border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)" }}>
              Créer le site du client
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// 🔴 P-MARIO-01 — BLOC CLIENT : QUI est le client (type, identité, contact, identifiant
//    légal, adresse du client / siège). ⚠️ L'adresse du CLIENT ⛔ n'est PAS le lieu des
//    travaux : les lieux vivent dans la section « Sites / Adresses d'intervention »
//    (règle d'OMV : siège bénéficiaire ≠ localisation des travaux).
//    Règles et formats : SOURCE UNIQUE `utils/clientFormat` (comme la fiche client).
function BlocClient({ client, repli, onOuvrir }) {
  const { items: typesClient } = useConfigList("client_types");
  const morale = estPersonneMorale(client.type_client);
  const nom = (morale && (client.raison_sociale || client.societe)) || formatClientFullName(client) || repli || "—";
  const adresse = formatClientAddress(client);
  const ligne = (testId, libelle, valeur) => valeur ? (
    <div className="text-sm" data-testid={testId} style={{ color: "var(--oh-text-secondary)" }}>
      <span style={{ color: "var(--oh-text-muted)" }}>{libelle} : </span>{valeur}
    </div>
  ) : null;
  return (
    <div data-testid="artisan-client-bloc" className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        {client.type_client && (
          <span data-testid="artisan-client-type" className="text-xs rounded-lg px-2"
                style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                         border: "0.5px solid var(--oh-info-border)" }}>
            {libelleTypeClient(typesClient, client.type_client)}
          </span>
        )}
        <span data-testid="artisan-client" style={{ color: "var(--oh-text)", fontWeight: 500 }}>{nom}</span>
        {onOuvrir && (
          <button type="button" data-testid="artisan-client-ouvrir" onClick={onOuvrir} className="text-sm underline"
                  style={{ color: "var(--oh-accent-text)", background: "none", border: "none", padding: 0 }}>
            Voir la fiche client
          </button>
        )}
      </div>
      {ligne("artisan-client-telephone", "Téléphone", client.telephone)}
      {ligne("artisan-client-email", "Email", client.email)}
      {morale && ligne("artisan-client-siret", "SIRET", client.siret)}
      {morale && !client.siret && ligne("artisan-client-siren", "SIREN", client.siren)}
      {ligne("artisan-client-adresse", morale ? "Siège" : "Adresse du client", adresse)}
    </div>
  );
}

export function DossierArtisanView({ dossier, dossierId, tenantId, documents, historique,
                                     onBack, onUpdate, onNavigate }) {
  const [voirHistorique, setVoirHistorique] = useState(false);
  const client = dossier?.client || {};
  const nomClient = client.raison_sociale || [client.prenom, client.nom].filter(Boolean).join(" ")
                    || dossier?.client_nom || "—";
  const reference = dossier?.reference || dossierId;

  return (
    <div className="space-y-4" data-testid="dossier-artisan">
      <div style={CARTE} className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            {onBack && (
              <button type="button" onClick={onBack} aria-label="Retour" className="text-sm mb-1"
                      style={{ color: "var(--oh-text-secondary)", background: "none", border: "none", padding: 0 }}>
                ← Retour
              </button>
            )}
            <div data-testid="artisan-reference" style={{ color: "var(--oh-text)", fontWeight: 500, fontSize: 18 }}>
              {reference}
            </div>
            <BlocClient client={client} repli={dossier?.client_nom}
                        onOuvrir={dossier?.client_id && onNavigate ? () => onNavigate("client", { id: dossier.client_id }) : null} />
          </div>
          <button type="button" onClick={() => setVoirHistorique(true)} className="rounded-lg px-3 text-sm"
                  style={{ height: 36, background: "transparent", border: "0.5px solid var(--oh-border)",
                           color: "var(--oh-text-secondary)" }}>
            Historique
          </button>
        </div>
        <BlocSite dossier={dossier} dossierId={dossierId} onUpdate={onUpdate} />
        <SuiviEtapesArtisan dossier={dossier} />
      </div>

      {dossier?.client_id && (
        <SitesDuClient clientId={dossier.client_id} siteCourantId={dossier.site_id}
                       titre="Sites / Adresses d'intervention"
                       onOpenDossier={(id) => onNavigate?.("dossier", { id })} />
      )}
      <div style={CARTE}><ListeDevisDossier dossierId={dossierId} onNavigate={onNavigate} /></div>
      <div style={CARTE}><EmplacementVisites dossierId={dossierId} onNavigate={onNavigate} /></div>
      <div style={CARTE}>
        <DocumentsForm dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} documents={documents}
                       verrouille={dossier?.verrouille} onUpdate={onUpdate}
                       clientNom={nomClient} dossierRef={reference} />
      </div>
      <CommentairesDossier dossierId={dossierId} tenantId={tenantId} verrouille={dossier?.verrouille} />
      {voirHistorique && (
        <HistoriqueModal historique={historique || []} onClose={() => setVoirHistorique(false)} />
      )}
    </div>
  );
}

export default DossierArtisanView;
